import {
  useMutation,
  useQueryClient,
  UseMutationResult,
} from "@tanstack/react-query";
import { LOG_EVENT } from "../api/queries";
import { graphQLClient } from "../graphqlClient";
import { v4 as uuidv4 } from "uuid";
import sessionStore from "../store/sessionStore";
import { usePostHog } from "posthog-js/react";

export const EVENT_TYPES = {
  LOGGED_IN: "logged-in",
  LADDER_COMPLETION: "ladder-completion",
  CLIMB_COMPLETION: "climb-completion",
  SITE_VISIT_CREATION: "site-visit-creation",
  // CERTIFICATE_COMPLETION: "certificate-completion", // this one is heandled in the pipeline resolver.
  HEARD_OF_HAT: "heard-of-hat",
  SAVED_HAT: "saved-hat",
  APPLICATION_ERROR: "application-error",
  EARNED_XP: "earned-xp",
  ACTIVITY_COMPLETION: "activity-completion",
  CONTENT_COMPLETION: "content-completion",
  CLUSTER_CONTENT_COMPLETED: "cluster-content-completed",
  RESOURCE_DOWNLOAD: "resource-download",
  HAT_EXPLORED: "hat-explored",
  FACET_CHANGED: "facet-changed",
  WATCHED_VIDEO: "watched-video",
  LISTENED_TO_AUDIO: "listened-to-audio",
  PROFILE_ONBOARD_COMPLETED: "profile-onboard-completed",
  HAT_RATED: "hat-rated",
  ADD_BUILDING_BLOCK: "add-building-block",
  PATHWAY_RATED: "pathway-rated",
  RECOMMENDED_CLUSTER_FOUND: "recommended-cluster-found",
  ONBOARD_SOMETHING_ELSE: "onboard-something-else",
  CLIMBER_RESET: "climber-reset",
  CAREER_CONFIDENCE_CHECKIN: "career-confidence-checkin",
} as const;

export type EventType = (typeof EVENT_TYPES)[keyof typeof EVENT_TYPES];

// What events should we be sending to posthog?
const POSTHOG_WHITE_LIST: readonly EventType[] = [
  EVENT_TYPES.HAT_EXPLORED,
  EVENT_TYPES.PROFILE_ONBOARD_COMPLETED,
  EVENT_TYPES.CLUSTER_CONTENT_COMPLETED,
  // EVENT_TYPES.CLIMB_COMPLETION, <-- Not called through useReporting, see queries.js
] as const;

interface LogEventMetadata {
  [key: string]: string | number | boolean | null | undefined;
}

interface LogEventInput {
  whatId?: string | null;
  eventType: EventType;
  maxScore?: number | null;
  score?: number | null;
  result?: string | null;
  metadata?: LogEventMetadata;
}

interface LogEventResponse {
  logEvent: string;
}

interface RecentEvent {
  id: string;
  eventType: EventType;
  data: string;
  timestamp: string;
}

export const useReporting = () => {
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  /*
    recentEvents: This is a bit annoying.
    When we log an event, it takes a bit of time to go through the pipeline and get saved to dynamo.
    While that's happening, we might try to getNextSteps, which needs to know about that event.
    
    So we'll store the event in a local array and when we request next steps, we'll pass all our recent events along. 
    The get next steps resolver will use our events if they're not already in dynamo. It can check if they're there
    by checking the uuid.
  */

  const logEvent = async (input: LogEventInput): Promise<LogEventResponse> => {
    const {
      whatId = null,
      eventType,
      maxScore = null,
      score = null,
      result = null,
      metadata = {},
    } = input;

    const uuid = uuidv4();
    const graphQLInput = {
      whatId,
      eventType,
      maxScore,
      score,
      result,
      metadata: JSON.stringify({
        ...metadata,
        uuid,
      }),
    };

    // Store the event in the format expected by the next steps processor
    const recentEvent: RecentEvent = {
      id: `recent-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      eventType,
      data: JSON.stringify({
        ...metadata,
        result,
        score: maxScore ? score : undefined,
        maxScore: maxScore || undefined,
        whatId,
        uuid, // UUID goes in the data for deduplication checking
      }),
      timestamp: new Date().toISOString(),
    };

    sessionStore.addRecentEvent(recentEvent);

    if (POSTHOG_WHITE_LIST.includes(eventType)) {
      posthog.capture(eventType, {
        whatId,
        eventType,
        maxScore,
        score,
        result,
        uuid,
        ...metadata,
      });
    }

    return graphQLClient.request<LogEventResponse>(LOG_EVENT, {
      input: graphQLInput,
    });
  };

  const mutation = useMutation<LogEventResponse, Error, LogEventInput>({
    mutationFn: logEvent,
    retry: true,
    onError: (error) => {
      console.error("Error logging event:", error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["nextSteps"] });
    },
  });

  const reportClimberReset = async (): Promise<LogEventResponse> => {
    return mutation.mutateAsync({
      eventType: EVENT_TYPES.CLIMBER_RESET,
    });
  };

  /**
   * User logged into the application
   */
  const reportLogin = async (): Promise<LogEventResponse> => {
    return mutation.mutateAsync({
      eventType: EVENT_TYPES.LOGGED_IN,
    });
  };

  /**
   * Completed a career ladder
   * @param ladderId - ID of the completed ladder
   * @param score - Score achieved on the ladder
   * @param maxScore - Maximum possible score for the ladder
   * @param result - Result of the ladder completion
   * @param time - Time taken to complete the ladder
   * @param learnositySessionId - the session id from learnosity
   * @param reportingFields - map of key:value pairs for fields with ReportMe tags on them
   */
  const reportLadderCompletion = async (
    ladderId: string,
    score: number,
    maxScore: number,
    result: string = "success",
    time: number = 0,
    learnositySessionId: string | null = null,
    reportingFields: LogEventMetadata = {},
  ): Promise<LogEventResponse> => {
    return mutation.mutateAsync({
      whatId: "LADDER-" + ladderId,
      eventType: EVENT_TYPES.LADDER_COMPLETION,
      score,
      maxScore,
      result,
      metadata: { time, ladderId, learnositySessionId, ...reportingFields },
    });
  };

  /**
   * Completed a career climb
   * @param climbId - ID of the completed climb
   * @param wasAssigned - Whether the climb was assigned to the user
   */
  const reportClimbCompletion = async (
    climbId: string,
    wasAssigned: boolean,
  ): Promise<LogEventResponse> => {
    return mutation.mutateAsync({
      whatId: "CLIMB-" + climbId,
      eventType: EVENT_TYPES.CLIMB_COMPLETION,
      metadata: { wasAssigned, climbId },
    });
  };

  /**
   * User indicated awareness of a career hat
   * @param hatId - ID of the career hat
   * @param heardOf - Whether the user has heard of this hat
   * @param greatFor - RIASEC letters indicating what this hat is great for
   * @param themes - CSV of theme IDs in format 0001,0002,0003
   */
  const reportHeardOfHat = async (
    hatId: string,
    heardOf: boolean,
    greatFor: string | null = null,
    themes: string | null = null,
    clusters: string | null = null,
    workValues: string | null = null,
  ): Promise<LogEventResponse> => {
    return mutation.mutateAsync({
      whatId: "HAT-" + hatId,
      eventType: EVENT_TYPES.HEARD_OF_HAT,
      result: heardOf ? "yes" : "no",
      metadata: { greatFor, themes, hatId, clusters, workValues },
    });
  };

  /**
   * User saved a career hat
   * @param hatId - ID of the career hat
   * @param saved - Whether the hat was saved or unsaved
   * @param greatFor - RIASEC letters indicating what this hat is great for
   * @param themes - CSV of theme IDs in format 0001,0002,0003
   */
  const reportSavedHat = async (
    hatId: string,
    saved: boolean,
    greatFor: string | null = null,
    themes: string | null = null,
    clusters: string | null = null,
    workValues: string | null = null,
  ): Promise<LogEventResponse> => {
    return mutation.mutateAsync({
      whatId: "HAT-" + hatId,
      eventType: EVENT_TYPES.SAVED_HAT,
      result: saved ? "yes" : "no",
      metadata: { greatFor, themes, hatId, clusters, workValues },
    });
  };

  /**
   * Log application errors
   * @param errorDetails - Details about the error that occurred
   */
  const reportError = async (
    errorDetails: LogEventMetadata,
  ): Promise<LogEventResponse> => {
    return mutation.mutateAsync({
      eventType: EVENT_TYPES.APPLICATION_ERROR,
      metadata: errorDetails,
    });
  };

  /**
   * User earned experience points
   * @param xp - Amount of XP earned
   * @param totalXp - Total XP the user now has
   * @param reason - Reason for earning the XP
   */
  const reportXPEarned = async (
    xp: number,
    totalXp: number,
    reason: string,
  ): Promise<LogEventResponse> => {
    return mutation.mutateAsync({
      eventType: EVENT_TYPES.EARNED_XP,
      score: xp,
      maxScore: totalXp,
      metadata: { reason },
    });
  };

  /**
   * Completed an activity
   * @param activityId - ID of the completed activity
   * @param result - Result of the activity completion
   * @param time - Time taken to complete the activity
   */
  const reportActivityCompletion = async (
    activityId: string,
    result: string,
    time: number,
  ): Promise<LogEventResponse> => {
    return mutation.mutateAsync({
      whatId: "ACTIVITY-" + activityId,
      eventType: EVENT_TYPES.ACTIVITY_COMPLETION,
      result,
      metadata: { time, activityId },
    });
  };

  /**
   * Explored a career hat by completing all the perks/quirks
   * @param hatId - ID of the explored hat
   * @param greatFor - RIASEC letters indicating what this hat is great for
   * @param themes - CSV of theme IDs in format 0001,0002,0003
   */
  const reportHatExplored = async (
    hatId: string,
    greatFor: string,
    themes: string | null = null,
    clusters: string | null = null,
    firstTime: boolean = false,
  ): Promise<LogEventResponse> => {
    return mutation.mutateAsync({
      whatId: "HAT-" + hatId,
      eventType: EVENT_TYPES.HAT_EXPLORED,
      metadata: {
        greatFor,
        themes,
        hatId,
        clusters,
        firstTime: firstTime ? "true" : "false",
      },
    });
  };

  /**
   * Completed content consumption
   * @param contentId - ID of the content
   * @param blockType - Type of content block
   * @param contentType - Type of content
   * @param contentLevel - Level of the content
   * @param parentId - ID of the parent content
   */
  const recordContentComplete = async (
    contentId: string,
    blockType: string,
    contentType: string,
    contentLevel: string,
    parentId: string,
  ): Promise<LogEventResponse> => {
    return mutation.mutateAsync({
      whatId: "CONTENT-" + contentId,
      eventType: EVENT_TYPES.CONTENT_COMPLETION,
      result: "success",
      metadata: {
        blockType,
        contentType,
        contentLevel,
        parentId,
        contentId,
      },
    });
  };

  /**
   * Completed all content within a cluster
   * @param clusterId - ID of the cluster
   */
  const reportClusterContentCompleted = async (
    clusterId: string,
  ): Promise<LogEventResponse> => {
    return mutation.mutateAsync({
      whatId: "CLUSTER-" + clusterId,
      eventType: EVENT_TYPES.CLUSTER_CONTENT_COMPLETED,
      result: "success",
      metadata: {
        clusterId,
      },
    });
  };

  /**
   * A recommended cluster has been found for the climber
   * @param clusterId - ID of the recommended cluster
   */
  const reportRecommendedClusterFound = async (
    clusterId: string,
  ): Promise<LogEventResponse> => {
    return mutation.mutateAsync({
      whatId: "CLUSTER-" + clusterId,
      eventType: EVENT_TYPES.RECOMMENDED_CLUSTER_FOUND,
      result: "success",
      metadata: { clusterId },
    });
  };

  /**
   * Downloaded a resource
   * @param resourceId - ID of the downloaded resource
   * @param resourceName - Name of the resource
   * @param resourceType - Type of the resource
   * @param category - Category of the resource
   * @param filesize - Size of the file in bytes
   * @param mimeType - MIME type of the file
   */
  const reportResourceDownload = async (
    resourceId: string,
    resourceName: string,
    resourceType: string,
    category: string,
    filesize: number,
    mimeType: string,
  ): Promise<LogEventResponse> => {
    return mutation.mutateAsync({
      whatId: "RESOURCE-" + resourceId,
      eventType: EVENT_TYPES.RESOURCE_DOWNLOAD,
      result: "success",
      metadata: {
        resourceName,
        resourceType,
        category,
        filesize,
        mimeType,
        resourceId,
      },
    });
  };

  /**
   * Occurs when a climber's facet changes for any reason
   * @param facetType - Type of facet that changed
   * @param currentScore - Current score of the facet
   * @param maxScore - Maximum possible score for the facet
   * percentage is auto-calculated from currentScore and maxScore
   */
  const reportFacetChanged = async (
    facetType: string,
    currentScore: number,
    maxScore: number,
  ): Promise<LogEventResponse> => {
    return mutation.mutateAsync({
      eventType: EVENT_TYPES.FACET_CHANGED,
      score: currentScore,
      maxScore: maxScore,
      metadata: {
        facetType,
        percentage: Math.round((currentScore / maxScore) * 100),
      },
    });
  };

  /**
   * Watched the given video
   * @param videoId - ID of the video that was watched
   */
  const reportWatchedVideo = async (
    videoId: string,
  ): Promise<LogEventResponse> => {
    return mutation.mutateAsync({
      whatId: "VIDEO-" + videoId,
      eventType: EVENT_TYPES.WATCHED_VIDEO,
      result: "success",
      metadata: { videoId },
    });
  };

  const reportListenedToAudio = async (
    audioId: string,
  ): Promise<LogEventResponse> => {
    return mutation.mutateAsync({
      whatId: "AUDIO-" + audioId,
      eventType: EVENT_TYPES.LISTENED_TO_AUDIO,
      result: "success",
      metadata: { audioId },
    });
  };

  /**
   * The profile onboarding has been completed
   * @param careerStage - Career stage of the user
   * @param careerConfidence - Career confidence level
   * @param topThemes - CSV of theme IDs in format 0001,0002,0003
   */
  const reportProfileOnboardCompleted = async (
    careerStage: string,
    careerConfidence: string,
    topThemes: string,
  ): Promise<LogEventResponse> => {
    return mutation.mutateAsync({
      eventType: EVENT_TYPES.PROFILE_ONBOARD_COMPLETED,
      result: "success",
      metadata: { careerStage, careerConfidence, topThemes },
    });
  };

  /**
   * The climber has used the like/dislike/not for me rating widget
   * @param rating - The rating given (like/dislike/not for me)
   * @param hatId - ID of the hat being rated
   * @param greatFor - RIASEC letters, use contains operator (e.g., "RI")
   * @param themes - CSV of theme IDs in format 0001,0002,0003
   * @param firstTime - Whether this is the first time rating this hat
   */
  const reportHatRated = async (
    rating: string,
    hatId: string,
    greatFor: string,
    themes: string,
    firstTime: boolean,
    clusters: string | null = null,
    pathways: string | null = null,
    workValues: string | null = null,
  ): Promise<LogEventResponse> => {
    return mutation.mutateAsync({
      whatId: "HAT-" + hatId,
      eventType: EVENT_TYPES.HAT_RATED,
      result: rating,
      metadata: {
        greatFor,
        themes,
        firstTime,
        hatId,
        clusters,
        pathways,
        workValues,
      },
    });
  };

  /**
   * Happens when the climber creates a new building block
   * @param blockName - Name of the building block created
   * @param blockCategory - Category of the building block
   */
  const reportAddBuildingBlock = async (
    blockName: string,
    blockCategory: string,
    firstTime: boolean = true,
  ): Promise<LogEventResponse> => {
    return mutation.mutateAsync({
      eventType: EVENT_TYPES.ADD_BUILDING_BLOCK,
      result: "success",
      metadata: { blockName, blockCategory, firstTime },
    });
  };

  /**
   * The climber has rated a pathway
   * @param pathwayId - ID of the pathway being rated
   * @param clusterId - ID of the cluster the pathway belongs to
   * @param firstTime - Whether this is the first time rating this pathway
   * @param result - The rating result
   */
  const reportPathwayRated = async (
    pathwayId: string,
    clusterId: string,
    firstTime: boolean,
    result: string,
  ): Promise<LogEventResponse> => {
    return mutation.mutateAsync({
      whatId: "PATHWAY-" + pathwayId,
      eventType: EVENT_TYPES.PATHWAY_RATED,
      result,
      metadata: {
        pathwayId,
        clusterId,
        firstTime: firstTime ? "true" : "false",
      },
    });
  };

  /**
   * Onboard something else has been completed
   * @param careerArea - The career area selected
   */
  const reportOnboardSomethingElse = async (
    careerArea: string,
  ): Promise<LogEventResponse> => {
    return mutation.mutateAsync({
      eventType: EVENT_TYPES.ONBOARD_SOMETHING_ELSE,
      result: "success",
      metadata: { careerArea },
    });
  };

  /**
   * A site visit has been created
   * @param visitId - ID of the created visit
   * @param climberId - ID of the climber the visit is for
   * @param worksite - ID of the worksite
   * @param visitDate - Date of the visit
   * @param visitTime - Time of the visit
   * @param visitorId - ID of the person who created the visit
   */
  const reportSiteVisitCreation = async (
    visitId: string,
    climberId: string,
    worksite: string,
    visitDate: string,
    visitTime: string,
    visitorId?: string | null,
  ): Promise<LogEventResponse> => {
    return mutation.mutateAsync({
      whatId: "VISIT-" + visitId,
      eventType: EVENT_TYPES.SITE_VISIT_CREATION,
      result: "success",
      metadata: {
        visitId,
        climberId,
        worksite,
        visitDate,
        visitTime,
        visitorId: visitorId || null,
      },
    });
  };

  /**
   * Career confidence check-in has been completed
   * @param currentStage - Current career stage (0-4)
   * @param currentConfidence - Current confidence level (0-3)
   * @param previousStage - Previous career stage from onboarding/last check-in
   * @param previousConfidence - Previous confidence level from onboarding/last check-in
   * @param changeExplanation - User's explanation for why their answer has/hasn't changed
   */
  const reportCareerConfidenceCheckin = async (
    currentStage: number,
    currentConfidence: number,
    previousStage: number | null,
    previousConfidence: number | null,
    changeExplanation?: string,
  ): Promise<LogEventResponse> => {
    return mutation.mutateAsync({
      eventType: EVENT_TYPES.CAREER_CONFIDENCE_CHECKIN,
      result: "success",
      metadata: {
        currentStage,
        currentConfidence,
        previousStage,
        previousConfidence,
        stageChanged: previousStage !== null && previousStage !== currentStage,
        confidenceChanged:
          previousConfidence !== null &&
          previousConfidence !== currentConfidence,
        changeExplanation: changeExplanation || null,
      },
    });
  };

  return {
    reportLogin,
    reportLadderCompletion,
    reportClimbCompletion,
    reportHatExplored,
    reportHeardOfHat,
    reportListenedToAudio,
    reportSavedHat,
    reportError,
    reportXPEarned,
    reportActivityCompletion,
    recordContentComplete,
    reportClusterContentCompleted,
    reportRecommendedClusterFound,
    reportResourceDownload,
    reportFacetChanged,
    reportWatchedVideo,
    reportProfileOnboardCompleted,
    reportHatRated,
    reportAddBuildingBlock,
    reportPathwayRated,
    reportOnboardSomethingElse,
    reportClimberReset,
    reportSiteVisitCreation,
    reportCareerConfidenceCheckin,
    logEvent: mutation,
    recentEvents: sessionStore.recentEvents,
    isLoading: mutation.isPending,
    error: mutation.error,
  };
};
